import type { SVGProps } from 'react';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'viewBox'> {
  size?: number;
}

const buildProps = ({ size = 18, ...props }: IconProps) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  ...props
});

export const BellIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <path d="M18 10a6 6 0 10-12 0c0 5-2 6-2 6h16s-2-1-2-6" />
    <path d="M13.73 21a2 2 0 01-3.46 0" />
  </svg>
);

export const UserIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

export const HelpIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <path d="M9.09 9a3 3 0 115.83 1c-.34.9-1.54 1.33-1.92 2.28-.12.31-.17.64-.17 1" />
    <line x1="12" y1="17" x2="12" y2="17.01" />
    <circle cx="12" cy="12" r="9" />
  </svg>
);

export const ExitIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

export const SunIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 3v2M12 19v2M5.64 5.64l1.42 1.42M16.94 16.94l1.42 1.42M3 12h2M19 12h2M5.64 18.36l1.42-1.42M16.94 7.06l1.42-1.42" />
  </svg>
);

export const MoonIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
  </svg>
);

export const HomeIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <path d="M3 11l9-8 9 8" />
    <path d="M5 13v6a2 2 0 002 2h10a2 2 0 002-2v-6" />
  </svg>
);

export const UsersIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <path d="M17 21v-2a4 4 0 00-3-3.87" />
    <path d="M7 21v-2a4 4 0 013-3.87" />
    <path d="M7 8a4 4 0 108 0" />
    <path d="M5.5 17a4 4 0 010-7" />
    <path d="M18.5 17a4 4 0 000-7" />
  </svg>
);

export const ShieldIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <path d="M12 22s7-4 7-10V5l-7-3-7 3v7c0 6 7 10 7 10z" />
  </svg>
);

export const NetworkIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <circle cx="12" cy="5" r="2" />
    <circle cx="5" cy="19" r="2" />
    <circle cx="19" cy="19" r="2" />
    <path d="M12 7v4.5M12 12l-5 7M12 12l5 7" />
  </svg>
);

export const GraphIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <circle cx="6" cy="6" r="2" />
    <circle cx="18" cy="6" r="2" />
    <circle cx="6" cy="18" r="2" />
    <circle cx="18" cy="18" r="2" />
    <path d="M8 6h8M6 8v8M18 8v8M8 18h8M8 8l8 8M16 8l-8 8" />
  </svg>
);

export const PathTraceIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <circle cx="5" cy="6" r="2" />
    <circle cx="12" cy="12" r="1.75" />
    <circle cx="19" cy="18" r="3.25" />
    <circle cx="19" cy="18" r="1" />
    <path d="M7 6h1.5a3.5 3.5 0 013.5 3.5V10" />
    <path d="M13.75 12H15a4 4 0 014 4" />
  </svg>
);

export const SwapHorizontalIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <path d="M16 3l4 4-4 4" />
    <path d="M20 7H8a4 4 0 00-4 4v1" />
    <path d="M8 21l-4-4 4-4" />
    <path d="M4 17h12a4 4 0 004-4v-1" />
  </svg>
);

export const ChartIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <path d="M4 19h17" />
    <path d="M6 17v-6" />
    <path d="M11 17V7" />
    <path d="M16 17v-4" />
  </svg>
);

export const LayersIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <path d="M12 2l9 4-9 4-9-4 9-4z" />
    <path d="M3 10l9 4 9-4" />
    <path d="M3 18l9 4 9-4" />
  </svg>
);

export const KeyIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <circle cx="7.5" cy="15.5" r="2.5" />
    <path d="M21 2l-7.5 7.5" />
    <path d="M10 10l-2 2" />
    <path d="M16 4l2 2" />
  </svg>
);

export const RadarIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <path d="M12 20a8 8 0 018-8" />
    <path d="M3 3l3 3" />
    <path d="M12 12l3 3" />
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

export const ChatIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <path d="M21 15a2 2 0 01-2 2H8l-4 4V5a2 2 0 012-2h13a2 2 0 012 2z" />
    <line x1="12" y1="11" x2="12" y2="11" />
    <line x1="16" y1="11" x2="16" y2="11" />
    <line x1="8" y1="11" x2="8" y2="11" />
  </svg>
);

export const HandshakeIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <path d="M3 11l4-4 4 4" />
    <path d="M21 11l-4-4-4 4" />
    <path d="M3 11v6l6 3 3-3 3 3 6-3v-6" />
    <path d="M9 14l3-3 3 3" />
  </svg>
);

export const LinkIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <path d="M10 13a5 5 0 007 0l2-2a5 5 0 00-7-7l-1 1" />
    <path d="M14 11a5 5 0 01-7 0l-2 2a5 5 0 007 7l1-1" />
  </svg>
);

export const ForkIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <circle cx="7" cy="5" r="2" />
    <circle cx="17" cy="5" r="2" />
    <circle cx="12" cy="19" r="2" />
    <path d="M7 7v3a5 5 0 005 5" />
    <path d="M17 7v3a5 5 0 01-5 5" />
    <line x1="12" y1="14" x2="12" y2="17" />
  </svg>
);

export const ActivityIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

export const PinIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <path d="M14 3l7 7" />
    <path d="M10 7l7 7" />
    <path d="M17 2l5 5-4 4-5-5z" />
    <path d="M9 9l6 6-7 7" />
    <path d="M4 20l5-5" />
  </svg>
);

export const CogIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
  </svg>
);

export const PlusIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

export const TrashIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
  </svg>
);

export const UploadIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <path d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
    <polyline points="7 9 12 4 17 9" />
    <line x1="12" y1="4" x2="12" y2="16" />
  </svg>
);

export const CheckCircleIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 12.5l2.5 2.5 4.5-5" />
  </svg>
);

export const XCircleIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M9 9l6 6M15 9l-6 6" />
  </svg>
);

export const FilterIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <path d="M4 4h16l-6 8v6l-4 2v-8L4 4z" />
  </svg>
);

export const RefreshIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10" />
    <path d="M20.49 15a9 9 0 01-14.85 3.36L1 14" />
  </svg>
);

export const PauseIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <rect x="6" y="4" width="4" height="16" rx="1" />
    <rect x="14" y="4" width="4" height="16" rx="1" />
  </svg>
);

export const PlayIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <polygon points="6 4 20 12 6 20 6 4" fill="currentColor" stroke="none" />
  </svg>
);

export const PencilIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <path d="M17 3a2.83 2.83 0 114 4L7 21l-4 1 1-4L17 3z" />
    <path d="M15 5l4 4" />
  </svg>
);

export const SaveIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
    <path d="M17 21v-8H7v8" />
    <path d="M7 3v5h8" />
  </svg>
);

export const CloseIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export const ExpandIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <polyline points="9 3 3 3 3 9" />
    <line x1="3" y1="3" x2="10" y2="10" />
    <polyline points="15 21 21 21 21 15" />
    <line x1="14" y1="14" x2="21" y2="21" />
    <polyline points="21 9 21 3 15 3" />
    <line x1="21" y1="3" x2="14" y2="10" />
    <polyline points="3 15 3 21 9 21" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

export const CompressIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <polyline points="9 3 9 9 3 9" />
    <line x1="9" y1="9" x2="3" y2="3" />
    <polyline points="15 21 15 15 21 15" />
    <line x1="15" y1="15" x2="21" y2="21" />
    <polyline points="15 3 15 9 21 9" />
    <line x1="15" y1="9" x2="21" y2="3" />
    <polyline points="9 21 9 15 3 15" />
    <line x1="9" y1="15" x2="3" y2="21" />
  </svg>
);

export const DownloadIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

export const CopyIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <rect x="5" y="11" width="10" height="10" rx="2" />
    <path d="M17 13h1a2 2 0 002-2V6a2 2 0 00-2-2h-7a2 2 0 00-2 2v1" />
  </svg>
);

export const ChevronLeftIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

export const ChevronRightIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

export const EyeIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export const EyeOffIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <path d="M17.94 17.94A10.94 10.94 0 0112 19c-7 0-11-7-11-7a20 20 0 014.22-5.88" />
    <path d="M1 1l22 22" />
    <path d="M9.53 9.53a3 3 0 104.24 4.24" />
    <path d="M14.47 14.47L12 12" />
    <path d="M17.94 6.06A10.94 10.94 0 0123 12s-4 7-11 7" />
  </svg>
);

export const TerminalIcon = (props: IconProps) => (
  <svg {...buildProps(props)}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M7 9l3 3-3 3" />
    <line x1="12" y1="15" x2="16" y2="15" />
  </svg>
);
